import { useEffect, useMemo, useState } from 'react';
import { CoreBox, CoreTooltip, CoreTypography } from '@youscience/core';
import { TeacherRecommendationStatus, Members, FormNames } from '@youscience/commonapp-service-common';

import useCommonAppAdminStore from '@store/CommonAppAdminStore';
import useRecommenderWorkspaceStore from '@store/RecommenderWorkspaceStore';

import { TEACHER_EVALUATION_STATUS_DATA } from '@constants/recommenderWorkspace';

import { counselorFormPages } from '@pages/StudentDetailsPage/components/CommonAppApplicationForms/CommonAppApplicationForms';
import { useApplicantFormsMetadata } from '@pages/StudentDetailsPage/hooks/useApplicantFormsMetadata';
import { FormAvailabilityStatus } from '@pages/StudentDetailsPage/components/common/enums';

import { Chip } from '@components/Chip';
import ActionButton from '@components/ActionButton';

import ReviewsIcon from '@images/Recommender/reviews.svg?react';
import ApprovalIcon from '@images/Recommender/approval.svg?react';
import EmailIcon from '@images/email.svg?react';
import LocationIcon from '@images/location.svg?react';

import SchoolCardHeader, { renderInfoItem } from './components/SchoolCardHeader';
import TeacherEvaluationPdf from './components/TeacherEvaluationPdf';
import { FormMetadata } from '../common/interfaces';
import { NOT_YET_AVAILABLE_CARD_TOOLTIP } from '../common/constants';
import { getFormStatusTextAndIcon } from '../common/formUtils';

import {
  sxStyles,
  StyledCardWrap,
  StyledRequirementItem,
  StyledTeacherInfo,
  StyledInfoItemsWrap,
  StyledRecommendersWrap,
} from './SchoolCard.styles';

interface SchoolCardProps {
  card: Members;
  applicantFormsMetadata: Record<FormNames, FormMetadata>;
  studentName: string;
  openedSchoolCardId: number | null;
  setOpenedSchoolCardId: (id: number | null) => void;
}

export const SchoolCard = (props: SchoolCardProps) => {
  const { card, studentName, openedSchoolCardId, setOpenedSchoolCardId } = props;

  const [isFullCard, setIsFullCard] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [reviewTeacherData, setReviewTeacherData] = useState<{ recommenderId: number; teacherFullName: string } | null>(
    null,
  );

  const { getRoles } = useCommonAppAdminStore((store) => store);
  const { setActiveTabs } = useRecommenderWorkspaceStore();
  const { formsMetadata } = useApplicantFormsMetadata();

  const applicationFormTabValues = useMemo(() => {
    const applicationForms: FormNames[] = [];
    const tabValuesWithUpdatedIndex: Record<FormNames, number> = {} as Record<FormNames, number>;

    counselorFormPages.forEach((page) => {
      if (!formsMetadata[page.name]) {
        return null;
      } else {
        applicationForms.push(page.name);
      }
    });

    applicationForms.forEach((item, index) => {
      tabValuesWithUpdatedIndex[item] = index;
    });

    return tabValuesWithUpdatedIndex;
  }, [formsMetadata]);

  const cardHeaderStatus = useMemo(() => {
    if (card.teachers.length === 0) return null;

    const isPending = card.teachers.some((item) => item.recommendation.status === TeacherRecommendationStatus.PENDING);
    const isRejected = card.teachers.some(
      (item) => item.recommendation.status === TeacherRecommendationStatus.REJECTED,
    );
    const isSubmitted = card.teachers.some(
      (item) => item.recommendation.status === TeacherRecommendationStatus.SUBMITTED,
    );

    return (
      (isPending && TeacherRecommendationStatus.PENDING) ||
      (isRejected && TeacherRecommendationStatus.REJECTED) ||
      (isSubmitted && TeacherRecommendationStatus.SUBMITTED) ||
      null
    );
  }, [card]);

  const handleOpenPreview = (recommenderId: number, teacherFullName: string) => () => {
    setIsPreviewOpen(true);
    setReviewTeacherData({
      recommenderId,
      teacherFullName,
    });
  };

  const handleClosePreview = () => {
    setIsPreviewOpen(false);
  };

  const handeOpenApplicationForm = (formName: FormNames) => () => {
    setActiveTabs({ activePageTab: 1, activeApplicationForm: applicationFormTabValues[formName] });

    window.scrollTo(0, 0);
  };

  const orderedCounselorForms = [
    FormNames.SECONDARY_REPORT,
    FormNames.FEE_WAIVER,
    FormNames.EARLY_DECISION,
    FormNames.EARLY_DECISION2,
    FormNames.RECOMMENDATION,
    FormNames.OPTIONAL_REPORT,
    FormNames.OPTIONAL_REPORT2,
    FormNames.MIDYEAR_REPORT,
    FormNames.FINAL_REPORT,
  ];

  const sortedCounselorForms = useMemo(() => {
    if (!card.counselor?.forms) return [];

    return [...card.counselor.forms].sort((a, b) => {
      const aIndex = orderedCounselorForms.indexOf(a.type as FormNames);
      const bIndex = orderedCounselorForms.indexOf(b.type as FormNames);
      return (aIndex === -1 ? Infinity : aIndex) - (bIndex === -1 ? Infinity : bIndex);
    });
  }, [card.counselor?.forms]);

  useEffect(() => {
    const getData = async () => {
      await getRoles();
    };

    void getData();
  }, []);

  return (
    <>
      <StyledCardWrap>
        <SchoolCardHeader
          isFullCard={isFullCard}
          setIsFullCard={setIsFullCard}
          card={card}
          cardHeaderStatus={cardHeaderStatus}
          openedSchoolCardId={openedSchoolCardId}
          setOpenedSchoolCardId={setOpenedSchoolCardId}
        />

        {isFullCard && openedSchoolCardId === card.memberId ? (
          <CoreBox sx={sxStyles.contentWrap}>
            <CoreBox>
              <CoreTypography variant="subtitle2">Contact information</CoreTypography>

              <StyledInfoItemsWrap>
                {renderInfoItem('Email', card.contactEmail ?? '', <EmailIcon />)}

                <CoreBox sx={sxStyles.divider} />

                {renderInfoItem('Address', '450 Jane Stanford WayStanford, CA 94305–2004', <LocationIcon />)}
              </StyledInfoItemsWrap>
            </CoreBox>

            <CoreTypography variant="subtitle2">Counselor form requirements</CoreTypography>

            <CoreBox sx={sxStyles.requirementsList}>
              {sortedCounselorForms
                .filter((form) =>
                  formsMetadata[form.type]?.members?.find((member) => member.memberId === card.memberId),
                )
                .map((form) => {
                  const formName = form.type;
                  const renderStatus = getFormStatusTextAndIcon(formsMetadata[formName], card.memberId);

                  const availabilityStatus = renderStatus.text.split(' / ')[1];

                  const getTooltipTitle = () => {
                    switch (availabilityStatus) {
                      case FormAvailabilityStatus.NOT_AVAILABLE:
                        return `You have already submitted the final report for ${studentName}. This form is no longer available.`;
                      case FormAvailabilityStatus.NOT_YET_AVAILABLE:
                        return NOT_YET_AVAILABLE_CARD_TOOLTIP[formName];
                      default:
                        return '';
                    }
                  };

                  return (
                    <CoreTooltip
                      key={formName}
                      title={renderStatus.disabled && getTooltipTitle()}
                      placement="top"
                      disableInteractive
                    >
                      <StyledRequirementItem
                        isDisable={renderStatus.disabled}
                        onClick={!renderStatus.disabled ? handeOpenApplicationForm(formName) : undefined}
                      >
                        {renderStatus.icon}

                        <CoreTypography variant="subtitle1" component="h2">
                          {formName}
                        </CoreTypography>

                        <CoreTypography sx={sxStyles.greyColor} variant="body2">
                          {renderStatus.text}
                        </CoreTypography>
                      </StyledRequirementItem>
                    </CoreTooltip>
                  );
                })}
            </CoreBox>

            <StyledRecommendersWrap>
              <CoreTypography variant="subtitle2">{`${studentName}’s recommenders`}</CoreTypography>

              <CoreBox sx={sxStyles.teacherTitle}>
                <ReviewsIcon />

                <CoreTypography variant="subtitle1" component="h2">
                  Teacher
                </CoreTypography>
              </CoreBox>

              <CoreBox sx={{ display: 'flex' }}>
                <CoreBox sx={{ display: 'flex', gap: '0.25rem' }}>
                  <CoreTypography variant="subtitle2" component="h4">
                    Required
                  </CoreTypography>

                  <CoreTypography sx={sxStyles.greyColor} variant="body2">
                    {`${Math.min(card.teachers.length, card.minTeacherEval)}/${card.minTeacherEval}`}
                  </CoreTypography>
                </CoreBox>

                <CoreBox sx={sxStyles.divider} />

                <CoreBox sx={{ display: 'flex', gap: '0.25rem' }}>
                  <CoreTypography variant="subtitle2" component="h4">
                    Optional
                  </CoreTypography>

                  <CoreTypography sx={sxStyles.greyColor} variant="body2">
                    {`${Math.max(0, card.teachers.length - card.minTeacherEval)}/${
                      card.maxTeacherEval - card.minTeacherEval
                    }`}
                  </CoreTypography>
                </CoreBox>
              </CoreBox>

              {card.teachers.map((teacher, index) => (
                <CoreBox key={teacher.recommenderId} sx={sxStyles.teacherEvaluationWrap}>
                  <StyledTeacherInfo>
                    <CoreTypography sx={{ textTransform: 'capitalize' }}>
                      {`${index + 1}. ${teacher.firstName} ${teacher.lastName}, ${teacher.subject}`}
                    </CoreTypography>

                    <CoreBox sx={sxStyles.assignedDate}>
                      {teacher.recommendation.submittedDate ? (
                        <>
                          <CoreTypography variant="subtitle2" component="h4">
                            Assigned:&nbsp;
                          </CoreTypography>

                          <CoreTypography variant="body2">
                            {teacher.recommendation.submittedDate
                              ? new Date(teacher.recommendation.submittedDate).toLocaleDateString()
                              : ''}
                          </CoreTypography>

                          <CoreBox sx={sxStyles.divider} />
                        </>
                      ) : null}

                      {TEACHER_EVALUATION_STATUS_DATA[teacher.recommendation.status]?.title ? (
                        <Chip
                          title={TEACHER_EVALUATION_STATUS_DATA[teacher.recommendation.status]?.title}
                          size="medium"
                        />
                      ) : null}
                    </CoreBox>
                  </StyledTeacherInfo>

                  {teacher.recommendation.status === TeacherRecommendationStatus.PENDING ? (
                    <ActionButton
                      variant="outlined"
                      sx={{ display: 'flex', gap: '0.5rem' }}
                      onClick={handleOpenPreview(teacher.recommenderId, `${teacher.firstName} ${teacher.lastName}`)}
                    >
                      <ApprovalIcon />
                      Review and submit
                    </ActionButton>
                  ) : null}
                </CoreBox>
              ))}
            </StyledRecommendersWrap>
          </CoreBox>
        ) : null}
      </StyledCardWrap>

      {isPreviewOpen ? (
        <TeacherEvaluationPdf
          isPreviewOpen={isPreviewOpen}
          teacherFullName={reviewTeacherData?.teacherFullName}
          recommenderId={reviewTeacherData?.recommenderId}
          onClosePreview={handleClosePreview}
        />
      ) : null}
    </>
  );
};
